import { useMemo, useState } from 'react';
import { useGetSupplierData } from '../../Supplier/hooks/useGetSupplierData';
import { getWireLimits } from '../api/getWireLimits';
import { getMailLimits } from '../api/getMailLimits';
import { getWireFees } from '../api/getWireFees';
import { getMailFees } from '../api/getMailFees';
import { useStore } from '../../../store';
import { IError } from '../../../types';
import { getAchOutLimits } from '../api/getAchOutLimits';
import { postWireOutReview } from '../api/postWireOutReview';
import { postMailCheckReview } from '../api/postMailCheckReview';
import { useUploadGenericFile } from '../../../hooks/utilities/useUploadGenericFile';
import { getAchOutFees } from '../api/getAchOutFees';
import { postAchOutReview } from '../api/postAchOutReview';
import { getInternationalCombinedData } from '../api/getInternationalCombinedData';
import { useGetSupportedCountries } from './useGetSupportedCountries';
import { postInternationalWireOutReview } from '../api/postInternationalWireOut';

export const useMoveMoneyLogic = () => {
  const getPersonalAccount = useStore((state) => state.getPersonalAccount);
  const getMoveMoneyData = useStore((state) => state.getMoveMoneyData);
  const moveMoneyData = useStore((state) => state.moveMoneyData);
  const setMoveMoneyData = useStore((state) => state.setMoveMoneyData);
  const resetMoveMoneyData = useStore((state) => state.resetMoveMoneyData);
  const resetMoveMoneyDataForAnotherTransfer = useStore((state) => state.resetMoveMoneyDataForAnotherTransfer);

  const [wireLimitsError, setWireLimitsError] = useState<IError | null>(null);
  const [achLimitsError, setAchLimitsError] = useState<{ errCode: number; errMsg: string } | null>(null);

  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const { accountExternalId } = getPersonalAccount();

  /** Queries */
  const { refetch: getSupportedCountries, data: supportedCountries } = useGetSupportedCountries();
  const { refetch: getSupplierList } = useGetSupplierData({});
  const { refetch: fetchWireLimits } = getWireLimits((errData) => setWireLimitsError(errData));
  const { refetch: fetchMailLimits } = getMailLimits();
  const { refetch: fetchAchOutLimits } = getAchOutLimits({ queryKey: 'getAchOutLimits' }, (data) =>
    setAchLimitsError(data));
  const { refetch: fetchInternationalData, isRefetching: isFetchingInternational } = getInternationalCombinedData();
  const { refetch: fetchWireFees } = getWireFees();
  const { refetch: fetchMailFees } = getMailFees();
  const { refetch: fetchAchOutFees } = getAchOutFees({
    amount: moveMoneyData?.amount.toString() || '0',
    customerId: externalId,
  });
  /** ------- */
  /** Mutations */
  const { mutateAsync: postWireReview, isLoading: isLoadingWireReview } = postWireOutReview();
  const { mutateAsync: postInternationalWireReview, isLoading: isLoadingInternationalWireReview } =
    postInternationalWireOutReview();
  const { mutateAsync: postMailCheck, isLoading: isLoadingMailReview } = postMailCheckReview();
  const { mutateAsync: postUploadSnapShot, isLoading: isLoadingFileUpload } = useUploadGenericFile();
  const { mutateAsync: postAchOut, isLoading: isLoadingAchOutReview } = postAchOutReview();
  /** ----- */

  const isLoadingState = useMemo(
    () =>
      isLoadingWireReview ||
      isLoadingMailReview ||
      isLoadingFileUpload ||
      isLoadingAchOutReview ||
      isFetchingInternational ||
      isLoadingInternationalWireReview,
    [
      isLoadingWireReview,
      isLoadingMailReview,
      isLoadingFileUpload,
      isLoadingAchOutReview,
      isFetchingInternational,
      isLoadingInternationalWireReview,
    ],
  );

  return {
    getSupplierList,
    fetchWireLimits,
    fetchMailLimits,
    fetchAchOutLimits,
    fetchWireFees,
    fetchMailFees,
    getMoveMoneyData,
    moveMoneyData,
    setMoveMoneyData,
    resetMoveMoneyData,
    wireLimitsError,
    postWireReview,
    postMailCheck,
    postUploadSnapShot,
    postAchOut,
    isLoadingState,
    fetchAchOutFees,
    resetMoveMoneyDataForAnotherTransfer,
    achLimitsError,
    fetchInternationalData,
    getSupportedCountries,
    supportedCountries,
    postInternationalWireReview,
    externalId,
    accountExternalId,
  };
};
